import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Badge from '../components/Badge';
import SLAIndicator from '../components/SLAIndicator';
import Button from '../components/Button';
import { fmt } from '../utils/helpers';
import { STATUS_LABELS } from '../utils/constants';
import { usePendingCount } from '../hooks/useTickets';
import api from '../services/api';
import type { Ticket } from '../types';

interface ITStaff { id: string; name: string; }

interface Page { tickets: Ticket[]; total: number; page: number; limit: number; }

// Statuses that require attention — from initial submission through to assignment,
// so a ticket is prominent in this list its whole life until someone's working it.
const PENDING_STATUSES = ['PendingLineManager', 'PendingTopManagement', 'PendingITManager', 'Assigned'];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PendingLineManager',     label: STATUS_LABELS['PendingLineManager'] ?? 'Pending LM' },
  { value: 'PendingITManager',       label: STATUS_LABELS['PendingITManager'] ?? 'Pending IT Manager' },
  { value: 'PendingTopManagement',   label: STATUS_LABELS['PendingTopManagement'] ?? 'Pending Top Mgmt' },
  { value: 'Assigned',               label: STATUS_LABELS['Assigned'] ?? 'Assigned' },
  { value: 'InProgress',             label: STATUS_LABELS['InProgress'] ?? 'In Progress' },
  { value: 'Approved',               label: 'Approved' },
  { value: 'Completed',              label: STATUS_LABELS['Completed'] ?? 'Completed' },
  { value: 'Resolved',               label: STATUS_LABELS['Resolved'] ?? 'Resolved' },
  { value: 'ClosedCompleted',        label: STATUS_LABELS['ClosedCompleted'] ?? 'Closed – Resolved' },
  { value: 'ClosedCannotImplement',  label: STATUS_LABELS['ClosedCannotImplement'] ?? 'Closed – Not Resolved' },
  { value: 'Rejected',               label: 'Rejected' },
  { value: 'Closed',                 label: 'SR Closed' },
];

const PRIORITIES = ['', 'Critical', 'High', 'Medium', 'Low'];
const PAGE_SIZE  = 50;

const sel = 'text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-violet-600 bg-white';

// While a ticket is still awaiting approval, "View Details" should land exactly as if
// opened from the Approvals tab (read-only approval view, Approvals stays highlighted).
// Once it's finished (closed/rejected/etc.) there's no more Workspace action left to
// take, so it stays in All Tickets' own context instead of jumping to Workspace.
// Anything in between (Approved through Resolved) is live Workspace work.
const PENDING_APPROVAL_STATUSES = ['PendingLineManager', 'PendingITManager', 'PendingTopManagement'];
const TERMINAL_STATUSES = [
  'Closed', 'ClosedCompleted', 'ClosedCannotImplement', 'ClosedOutOfScope', 'ClosedDuplicate', 'Rejected',
];
function detailNavState(t: Ticket) {
  const activeNav =
    PENDING_APPROVAL_STATUSES.includes(t.status) ? '/approvals' :
    TERMINAL_STATUSES.includes(t.status)         ? '/all-tickets' :
    '/workspace';
  return { state: { activeNav } };
}

function TicketTable({
  tickets, navigate, emptyMsg,
}: { tickets: Ticket[]; navigate: (path: string, options?: { state?: any }) => void; emptyMsg: string }) {
  if (tickets.length === 0) {
    return <p className="text-xs text-gray-400 text-center py-6">{emptyMsg}</p>;
  }
  return (
    <>
      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-gray-50">
        {tickets.map(t => (
          <div
            key={t.id}
            className="p-4 cursor-pointer active:bg-gray-50"
            onClick={() => navigate(`/tickets/${t.id}`, detailNavState(t))}
          >
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <div className="min-w-0">
                <span className="font-mono text-xs font-semibold text-[#26148C] break-all">{t.id}</span>
                <p className="text-xs font-medium text-[#010E23] mt-0.5 truncate">{t.requester?.name || '—'}</p>
              </div>
              <Badge status={t.status} small />
            </div>
            <p className="text-xs text-gray-500 mb-2 truncate">
              {t.items?.[0]?.category || '—'} · {t.requester?.department || '—'} · {t.assignedTo?.name || 'Unassigned'}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold ${
                  t.priority === 'Critical' ? 'text-red-700' :
                  t.priority === 'High'     ? 'text-orange-600' :
                  t.priority === 'Medium'   ? 'text-yellow-600' : 'text-green-700'
                }`}>{t.priority}</span>
                <span className="text-[11px] text-gray-400">{fmt(t.submittedAt)}</span>
              </div>
              <SLAIndicator ticket={t} compact />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" style={{ minWidth: 960 }}>
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              {['Ref', 'Requester', 'Dept', 'Category', 'Priority', 'Assigned To', 'Submitted', 'Status', 'SLA', ''].map(h => (
                <th key={h} className="text-left text-xs font-semibold text-gray-400 px-4 py-2.5">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tickets.map(t => (
              <tr
                key={t.id}
                className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/tickets/${t.id}`, detailNavState(t))}
              >
                <td className="px-4 py-3 font-mono text-xs text-[#26148C]">{t.id}</td>
                <td className="px-4 py-3 text-xs font-medium text-[#010E23]">{t.requester?.name || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{t.requester?.department || '—'}</td>
                <td className="px-4 py-3 text-xs">{t.items?.[0]?.category || '—'}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium ${
                    t.priority === 'Critical' ? 'text-red-700' :
                    t.priority === 'High'     ? 'text-orange-600' :
                    t.priority === 'Medium'   ? 'text-yellow-600' : 'text-green-700'
                  }`}>{t.priority}</span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{t.assignedTo?.name || '—'}</td>
                <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmt(t.submittedAt)}</td>
                <td className="px-4 py-3"><Badge status={t.status} small /></td>
                <td className="px-4 py-3 min-w-[120px]"><SLAIndicator ticket={t} compact /></td>
                <td className="px-4 py-3">
                  <button
                    className="text-xs font-medium text-violet-700 bg-violet-50 px-3 py-1 rounded-lg hover:bg-violet-100 transition-colors whitespace-nowrap"
                    onClick={e => { e.stopPropagation(); navigate(`/tickets/${t.id}`, detailNavState(t)); }}
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function AllTickets() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { count: pendingCount, refetch: refetchCount } = usePendingCount(true);

  // Dashboard stat cards deep-link here with a status to pre-filter to
  // (e.g. navigate('/all-tickets', { state: { status: 'PendingITManager' } })).
  const [status,          setStatus]          = useState((location.state as { status?: string } | null)?.status ?? '');
  const [priority,        setPriority]        = useState('');
  const [assignedToId,    setAssignedToId]    = useState('');
  const [itStaff,         setITStaff]         = useState<ITStaff[]>([]);
  const [search,          setSearch]          = useState('');
  const [page,            setPage]            = useState(1);
  const [data,            setData]            = useState<Page | null>(null);
  const [loading,         setLoading]         = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    api.get<ITStaff[]>('/admin/users/it-staff-list').then(r => setITStaff(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, status, priority, assignedToId]);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
      if (debouncedSearch) params.set('search',       debouncedSearch);
      if (status)          params.set('status',       status);
      if (priority)        params.set('priority',     priority);
      if (assignedToId)    params.set('assignedToId', assignedToId);
      const r = await api.get<Page>(`/tickets/all?${params}`);
      setData(r.data);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [debouncedSearch, status, priority, assignedToId, page]);

  // Poll so approvals/assignments/closures made elsewhere show up here without a
  // manual refresh.
  useEffect(() => {
    load();
    const interval = setInterval(() => load({ silent: true }), 15000);
    return () => clearInterval(interval);
  }, [load]);

  const tickets    = data?.tickets    ?? [];
  const total      = data?.total      ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Split into pending action vs the rest
  const pendingTickets = tickets.filter(t => PENDING_STATUSES.includes(t.status));
  const otherTickets   = tickets.filter(t => !PENDING_STATUSES.includes(t.status));

  // When no filter is active, show the split view; when a filter is active, show flat list
  const showSplit = !status && !debouncedSearch && !priority && !assignedToId;

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-[#010E23]">All Tickets</h1>
        <span className="text-xs text-gray-400">{total} total</span>
      </div>

      {/* Pending Action banner — only shown when no filter active */}
      {showSplit && (
        <div className={`rounded-xl border p-4 flex items-center gap-4 ${
          pendingCount > 0
            ? 'bg-red-50 border-red-200'
            : 'bg-gray-50 border-gray-200'
        }`}>
          <div className={`text-xl font-bold rounded-full w-10 h-10 flex items-center justify-center flex-shrink-0 ${
            pendingCount > 0 ? 'bg-red-500 text-white' : 'bg-gray-300 text-gray-600'
          }`}>
            {pendingCount}
          </div>
          <div>
            <p className={`text-sm font-semibold ${pendingCount > 0 ? 'text-red-800' : 'text-gray-600'}`}>
              {pendingCount > 0 ? 'Pending Action' : 'No Pending Actions'}
            </p>
            <p className={`text-xs mt-0.5 ${pendingCount > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {pendingCount > 0
                ? `${pendingCount} ticket${pendingCount === 1 ? '' : 's'} requiring assignment or closure`
                : 'All tickets are assigned or closed'}
            </p>
          </div>
          <button
            onClick={() => { load(); refetchCount(); }}
            className="ml-auto text-xs text-gray-400 hover:text-gray-600 underline"
          >
            Refresh
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <input
          className={sel + ' flex-1 min-w-[140px]'}
          placeholder="Search ref, name, purpose…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className={sel} value={status} onChange={e => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select className={sel} value={priority} onChange={e => setPriority(e.target.value)}>
          {PRIORITIES.map(p => <option key={p} value={p}>{p || 'All Priorities'}</option>)}
        </select>
        <select className={sel} value={assignedToId} onChange={e => setAssignedToId(e.target.value)}>
          <option value="">All Assignees</option>
          {itStaff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {(status || priority || search || assignedToId) && (
          <button
            className="text-xs text-violet-600 hover:underline"
            onClick={() => { setStatus(''); setPriority(''); setSearch(''); setAssignedToId(''); }}
          >
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-16 flex justify-center">
          <div className="w-5 h-5 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : showSplit ? (
        /* ── Split view ── */
        <div className="space-y-6">

          {/* SECTION A — Pending Action */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-red-500" />
              <h2 className="text-sm font-semibold text-gray-700">
                Pending Action
                <span className="ml-2 text-xs font-normal text-gray-400">
                  ({pendingTickets.length})
                </span>
              </h2>
            </div>
            <div className={`rounded-xl border overflow-hidden ${
              pendingTickets.length > 0 ? 'border-orange-200 bg-amber-50/60' : 'border-gray-100 bg-white'
            }`}>
              <TicketTable
                tickets={pendingTickets}
                navigate={navigate}
                emptyMsg="No pending tickets — all up to date"
              />
            </div>
          </div>

          {/* SECTION B — All Other Tickets */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-gray-400" />
              <h2 className="text-sm font-semibold text-gray-700">
                All Tickets
                <span className="ml-2 text-xs font-normal text-gray-400">
                  ({otherTickets.length}{total > PAGE_SIZE ? '+' : ''})
                </span>
              </h2>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
              <TicketTable
                tickets={otherTickets}
                navigate={navigate}
                emptyMsg="No tickets yet"
              />
            </div>
          </div>

        </div>
      ) : (
        /* ── Flat view (filter active) ── */
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {tickets.length === 0 ? (
            <div className="py-16 text-center text-sm text-gray-400">No tickets found</div>
          ) : (
            <TicketTable tickets={tickets} navigate={navigate} emptyMsg="No tickets found" />
          )}
        </div>
      )}

      {/* Pagination */}
      {!showSplit && totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border border-gray-100 rounded-xl bg-gray-50/50">
          <span className="text-xs text-gray-400">
            {total === 0 ? 'No tickets' : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>← Prev</Button>
            <span className="text-xs text-gray-500 px-2">{page} / {totalPages}</span>
            <Button size="sm" variant="ghost" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next →</Button>
          </div>
        </div>
      )}
    </div>
  );
}
